import { useCallback, useEffect, useRef, useState } from "react";
import { useDailyLog } from "./useDailyLog";

export type DayNoteSaveStatus = "idle" | "saving" | "saved" | "error";

interface DayNoteDraft {
  html: string;
  dirty: boolean;
  savedAt: number;
}

const DRAFT_PREFIX = "otdesk.daynote.";

function draftKey(date: string): string {
  return `${DRAFT_PREFIX}${date}`;
}

function readDraft(date: string): DayNoteDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(date));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DayNoteDraft>;
    if (typeof parsed.html !== "string") return null;
    return { html: parsed.html, dirty: parsed.dirty === true, savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0 };
  } catch {
    // localStorage can be unavailable (private mode / quota). The draft mirror is
    // best-effort crash protection only; the server save remains the source of truth.
    return null;
  }
}

function writeDraft(date: string, draft: DayNoteDraft): void {
  try {
    localStorage.setItem(draftKey(date), JSON.stringify(draft));
  } catch {
    // Same as readDraft: never let a full/blocked localStorage take down the editor.
  }
}

/**
 * Day-note editing state with debounced autosave and a localStorage draft
 * mirror: every keystroke updates the mirror, the server PUT fires ~1s after
 * typing stops, and a dirty mirror is restored on the next open (crash
 * recovery). Failures are surfaced as a visible "error" status + retry.
 *
 * Day switches use stale-while-revalidate: the editor keeps showing the
 * previous day's note (no blank flash) until the selected day's note arrives,
 * then swaps in place. A dirty crash-recovery draft for the selected day
 * restores immediately.
 */
export function useDayNote(date: string) {
  const { query, save } = useDailyLog(date);
  // useMutation returns a new object every render, so keep it in a ref —
  // depending on it would change persist/flush identity every render and the
  // flush cleanup effect would re-run (and re-save) in a loop.
  const saveRef = useRef(save);
  saveRef.current = save;
  const [content, setContent] = useState<string | null>(null);
  const [status, setStatus] = useState<DayNoteSaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const timer = useRef<number | null>(null);
  const dirty = useRef(false);
  const latest = useRef<string | null>(null);
  // Which date `content` currently belongs to. Kept across day switches so the
  // editor can hold the previous day's note while the next day's loads.
  const contentDate = useRef<string | null>(null);

  const persist = useCallback(
    async (html: string) => {
      setStatus("saving");
      try {
        await saveRef.current.mutateAsync(html);
        dirty.current = false;
        const now = Date.now();
        setSavedAt(now);
        writeDraft(date, { html, dirty: false, savedAt: now });
        setStatus("saved");
      } catch {
        dirty.current = true;
        setStatus("error");
      }
    },
    [date],
  );

  // Switching days: clear transient state but keep the current content visible
  // until the new day's note resolves (the init effect below swaps it).
  useEffect(() => {
    setStatus("idle");
    setSavedAt(null);
    setRestoredDraft(false);
    dirty.current = false;
    latest.current = null;
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, [date]);

  // Initialise the editor for the selected day: restore a dirty crash-recovery
  // draft immediately, otherwise swap to the server note when it arrives.
  useEffect(() => {
    if (contentDate.current === date && content !== null) return;
    const draft = readDraft(date);
    if (draft && draft.dirty) {
      setContent(draft.html);
      latest.current = draft.html;
      dirty.current = true;
      contentDate.current = date;
      setRestoredDraft(true);
      timer.current = window.setTimeout(() => {
        void persist(draft.html);
      }, 800);
      return;
    }
    if (query.data) {
      setContent(query.data.notes);
      latest.current = query.data.notes;
      contentDate.current = date;
      dirty.current = false;
    }
    // No draft and no server note yet: keep whatever is currently visible —
    // this effect re-runs when query.data arrives.
  }, [content, date, persist, query.data]);

  // Flush any pending keystrokes when leaving the page.
  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (dirty.current && latest.current !== null) {
      void persist(latest.current);
    }
  }, [persist]);

  useEffect(() => () => flush(), [flush]);

  const handleChange = useCallback(
    (html: string) => {
      setContent(html);
      contentDate.current = date;
      latest.current = html;
      dirty.current = true;
      writeDraft(date, { html, dirty: true, savedAt: Date.now() });
      setStatus((s) => (s === "error" ? "error" : "idle"));
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        void persist(html);
      }, 1000);
    },
    [date, persist],
  );

  const retry = useCallback(() => {
    if (latest.current !== null) void persist(latest.current);
  }, [persist]);

  return { query, content, status, savedAt, restoredDraft, handleChange, retry };
}
